import { useEffect, useRef, useState } from 'react';
import type { Anime } from '../lib/types';
import { searchPool, displayTitle } from '../lib/game';
import { useApp } from '../app-context';

interface Props {
  excludeIds: Set<number>;
  disabled: boolean;
  onGuess: (a: Anime) => void;
}

export default function GuessSearch({ excludeIds, disabled, onGuess }: Props) {
  const { t } = useApp();
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const matches = q ? searchPool(q, excludeIds) : [];

  useEffect(() => setHi(0), [q]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setQ('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function pick(a: Anime) {
    onGuess(a);
    setQ('');
  }

  function onKey(e: React.KeyboardEvent) {
    if (!matches.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => (h + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(matches[hi]); }
    else if (e.key === 'Escape') setQ('');
  }

  return (
    <div className="search" ref={boxRef}>
      <input
        value={q}
        disabled={disabled}
        placeholder={t('input.placeholder')}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKey}
        aria-label="Guess an anime title"
      />
      <button disabled={disabled || !matches.length} onClick={() => matches.length && pick(matches[hi])}>
        {t('guess')}
      </button>
      {matches.length > 0 && (
        <div className="ac" role="listbox">
          {matches.map((a, i) => (
            <button key={a.id} className={i === hi ? 'hi' : ''} onClick={() => pick(a)} role="option" aria-selected={i === hi}>
              {a.cover && <img src={a.cover} alt="" loading="lazy" width={34} height={48} />}
              <span className="info">
                <span className="ttl">{displayTitle(a)}</span>
                <span className="meta">{a.year ?? '—'} · {a.studio ?? '—'}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
